'use client'

import { useEffect, useState } from 'react'

type MediaDoc = { id: number; url?: string; sizes?: Record<string, { url?: string }> }

const cache = new Map<number, string | null>()

/** Resolves a Media upload field's raw id (as stored in Payload admin form state) to a displayable URL. */
export function useMediaUrl(mediaId: unknown): string | null {
  const id = typeof mediaId === 'number' ? mediaId : typeof mediaId === 'string' && mediaId ? Number(mediaId) : null
  const [url, setUrl] = useState<string | null>(id != null ? (cache.get(id) ?? null) : null)

  useEffect(() => {
    if (id == null || Number.isNaN(id)) {
      setUrl(null)
      return
    }
    if (cache.has(id)) {
      setUrl(cache.get(id) ?? null)
      return
    }
    let cancelled = false
    fetch(`/api/media/${id}`)
      .then(res => (res.ok ? res.json() : null))
      .then((doc: MediaDoc | null) => {
        const resolved = doc?.sizes?.hero?.url || doc?.url || null
        cache.set(id, resolved)
        if (!cancelled) setUrl(resolved)
      })
      .catch(() => {
        if (!cancelled) setUrl(null)
      })
    return () => {
      cancelled = true
    }
  }, [id])

  return url
}

/** Resolves several Media ids at once, returning a map of id -> url (or null while unresolved/missing). */
export function useMediaUrls(mediaIds: number[]): Record<number, string | null> {
  const key = mediaIds.join(',')
  const [urls, setUrls] = useState<Record<number, string | null>>({})

  useEffect(() => {
    const ids = key ? key.split(',').map(Number) : []
    const missing = ids.filter(id => !cache.has(id))
    if (missing.length === 0) {
      setUrls(Object.fromEntries(ids.map(id => [id, cache.get(id) ?? null])))
      return
    }
    let cancelled = false
    Promise.all(
      missing.map(id =>
        fetch(`/api/media/${id}`)
          .then(res => (res.ok ? res.json() : null))
          .then((doc: MediaDoc | null) => {
            const resolved = doc?.sizes?.hero?.url || doc?.url || null
            cache.set(id, resolved)
          })
          .catch(() => cache.set(id, null)),
      ),
    ).then(() => {
      if (!cancelled) setUrls(Object.fromEntries(ids.map(id => [id, cache.get(id) ?? null])))
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return urls
}
